import {
  ConnectBalances,
  ConnectInstantPayoutsPromotion,
  ConnectNotificationBanner,
} from "@stripe/react-connect-js";
import { createFileRoute } from "@tanstack/react-router";
import { ShieldAlert } from "lucide-react";

import { PageWrapper } from "~/components/page-wrapper";
import { ConnectProvider } from "~/components/stripe/connect-provider";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Skeleton } from "~/components/ui/skeleton";
import { useEmployeePermissions } from "~/hooks/use-employee-permissions";
import { useActiveOrganization } from "~/integrations/vortex-auth/hooks";

export const Route = createFileRoute("/_authenticated/balances")({
  component: BalancesPage,
  pendingComponent: () => (
    <PageWrapper
      description="View your available balance and manage funds"
      title="Balances"
    >
      <div className="bg-card animate-pulse rounded-xl border">
        <div className="space-y-4 p-6">
          <div className="bg-muted h-6 w-32 rounded-md" />
          <div className="grid gap-4 md:grid-cols-2">
            <div className="border-border rounded-lg border p-4">
              <div className="bg-muted h-4 w-24 rounded" />
              <div className="bg-muted mt-2 h-10 w-40 rounded" />
            </div>
            <div className="border-border rounded-lg border p-4">
              <div className="bg-muted h-4 w-24 rounded" />
              <div className="bg-muted mt-2 h-10 w-40 rounded" />
            </div>
          </div>
          <div className="bg-muted h-9 w-32 rounded-md" />
        </div>
      </div>
    </PageWrapper>
  ),
});

function BalancesPage() {
  const { organization } = useActiveOrganization();
  const { hasPermission, isLoading, hasEmployee } = useEmployeePermissions();
  const canViewIntegrations = hasPermission("viewIntegrations");

  if (!organization) {
    return null;
  }

  // Loading state
  if (isLoading) {
    return (
      <PageWrapper
        description="View your available balance and manage funds"
        title="Balances"
      >
        <Skeleton className="h-96 w-full" />
      </PageWrapper>
    );
  }

  // No employee record linked
  if (!hasEmployee) {
    return (
      <PageWrapper
        description="View your available balance and manage funds"
        title="Balances"
      >
        <Alert>
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>No Employee Profile</AlertTitle>
          <AlertDescription>
            Your account is not linked to an employee profile. Contact your
            organization admin to set up your employee record with the
            appropriate permissions.
          </AlertDescription>
        </Alert>
      </PageWrapper>
    );
  }

  // No permission to view balances
  if (!canViewIntegrations) {
    return (
      <PageWrapper
        description="View your available balance and manage funds"
        title="Balances"
      >
        <Alert variant="destructive">
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>
            You don't have permission to view balance information. Contact your
            manager to request access.
          </AlertDescription>
        </Alert>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper
      description="View your available balance, pending funds, and manage payouts"
      title="Balances"
    >
      <div className="space-y-6">
        <ConnectProvider organizationId={organization.id}>
          <ConnectNotificationBanner />
        </ConnectProvider>

        {/* Instant Payouts Promotion - shows when eligible for instant payouts */}
        <ConnectProvider
          className="bg-card overflow-hidden rounded-xl border p-6"
          organizationId={organization.id}
        >
          <ConnectInstantPayoutsPromotion />
        </ConnectProvider>

        {/* Main Balances Component */}
        <ConnectProvider
          className="bg-card overflow-hidden rounded-xl border p-6"
          organizationId={organization.id}
        >
          <ConnectBalances />
        </ConnectProvider>
      </div>
    </PageWrapper>
  );
}
